import getBrowser, { getOS } from '@tv2/velour/libraries/utils/browserDetection';
import {
  AdVendor,
  CastDeviceVersion,
  Codec,
  Drm,
  DynamicRange,
  Format,
  HDCPVersion,
  MaxBitrate,
  MediaCapabilities,
  Resolution,
  SourceType,
} from '../types';
import {
  checkHDCP,
  getHDCPVersion,
  getHighestSupportedRobustness,
} from '../util/drm/deviceSecurity';

const browser = getBrowser();
const os = getOS();

enum Type {
  File = 'file',
  MediaSource = 'media-source',
}

enum VideoTypes {
  H265 = 'video/mp4; codecs="hev1.1.6.L153.B0"',
  H264 = 'video/mp4; codecs="avc1.42E01E"',
}

enum AudioTypes {
  MP3 = 'audio/mp3',
  AC3 = 'audio/mp4; codecs="ac-3"',
}

enum HdrMetadataType {
  'smpteSt2086' = 'smpteSt2086',
  'smpteSt2094-10' = 'smpteSt2094-10',
  'smpteSt2094-40' = 'smpteSt2094-40',
}

type ProbeConfig = {
  contentType?: VideoTypes | AudioTypes;
  width?: number;
  height?: number;
  bitrate?: number;
  framerate?: number;
  hdrMetadataType?: HdrMetadataType | undefined;
};

const probeMediaCapabilities = ({
  contentType = VideoTypes.H264,
  width = 800,
  height = 600,
  bitrate = 1000000,
  framerate = 25,
  hdrMetadataType,
}: ProbeConfig) => {
  if ('mediaCapabilities' in navigator === false) return Promise.resolve({ supported: false });

  return navigator.mediaCapabilities.decodingInfo({
    type: Type.MediaSource,

    video: {
      hdrMetadataType,
      contentType,
      width,
      height,
      bitrate,
      framerate,
    },
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const propeAudioCapabilities = ({ contentType = AudioTypes.MP3 }: ProbeConfig) => {
  return navigator.mediaCapabilities.decodingInfo({
    type: Type.File,
    audio: {
      contentType,
    },
  });
};

const getSupportedDRM = async () => {
  if ('requestMediaKeySystemAccess' in navigator === false) {
    if (browser === 'safari') return [Drm.FAIRPLAY];
    return [Drm.NONE];
  }

  const supportedDRM = new Set<Drm>();
  const keySystems = {
    'com.microsoft.playready': Drm.PLAYREADY,
    'com.microsoft.playready.recommendation': Drm.PLAYREADY,
    'com.widevine.alpha': Drm.WIDEVINE,
    'com.apple.fps.3_0': Drm.FAIRPLAY,
    'com.apple.fps.2_0': Drm.FAIRPLAY,
    'com.apple.fps.1_0': Drm.FAIRPLAY,
    'com.apple.fps': Drm.FAIRPLAY,
    'com.apple.fairplay': Drm.FAIRPLAY,
  };

  const basicVideoCapabilities = [{ contentType: VideoTypes.H264 }];

  const basicConfig = {
    initDataTypes: ['cenc'],
    videoCapabilities: basicVideoCapabilities,
  };

  const configs = [basicConfig];

  const testSystem = async ([keySystem, drm]: [string, Drm]) => {
    try {
      const access = await navigator.requestMediaKeySystemAccess(keySystem, configs);
      supportedDRM.add(drm);
      await access.createMediaKeys();
    } catch (e) {
      // Do nothing
    }
  };

  const tests = Object.entries(keySystems).map(keySystem => testSystem(keySystem));
  await Promise.all(tests);

  if (supportedDRM.size === 0 && browser === 'safari') return [Drm.FAIRPLAY];

  return [...supportedDRM.values()];
};

const getSupportedResolutions = async () => {
  const supportedDRM = await getSupportedDRM();
  const screenSupportsHD = await checkHDCP(supportedDRM, HDCPVersion.HD);
  const screenSupportsUHD = await checkHDCP(supportedDRM, HDCPVersion.UHD);

  let resolutions = [
    { width: 7680, height: 4320, supported: false, resolution: Resolution.R_4K },
    { width: 3840, height: 2160, supported: false, resolution: Resolution.R_4K },
    { width: 1920, height: 1080, supported: false, resolution: Resolution.R_1080P },
    { width: 1280, height: 720, supported: false, resolution: Resolution.R_720P },
  ];

  if (!screenSupportsHD) return [Resolution.R_720P];
  if (!screenSupportsUHD)
    resolutions = resolutions.filter(({ resolution }) => resolution !== Resolution.R_4K);

  const supportedResolutions: Promise<Partial<MediaCapabilitiesDecodingInfo>>[] = [];

  for (let i = 0; i < resolutions.length; i += 1) {
    const { width, height } = resolutions[i];
    supportedResolutions.push(probeMediaCapabilities({ width, height }));
  }

  const resolved = await Promise.all(supportedResolutions);

  for (let i = 0; i < resolutions.length; i += 1) {
    resolutions[i].supported = resolved[i].supported ?? false;
  }

  return [
    // remove duplicates with set
    ...new Set([
      // filter out unsupported resolutions
      ...resolutions.filter(({ supported }) => supported).map(({ resolution }) => resolution),
      // default to 720P
      Resolution.R_720P,
    ]),
  ];
};
const getSupportedCodecs = async () => {
  const codecs = new Array(Codec.H264);
  // If os is web and browser is Chrome, HEVC is not supported
  const isBrowserNotSupported = os === 'other' && browser === 'chrome';
  const supported =
    typeof MediaSource !== 'undefined' && (await MediaSource.isTypeSupported(VideoTypes.H265));
  if (supported && !isBrowserNotSupported) codecs.push(Codec.H265);
  return codecs;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getSupportedDynamicRange = async () => {
  const isScreenHDR = await globalThis.matchMedia('(dynamic-range: high)').matches;

  if (isScreenHDR) {
    return [DynamicRange.HDR, DynamicRange.SDR];
  }

  return [DynamicRange.SDR];
};

const getSupportedFormats = () => {
  if (browser === 'safari') return [Format.HLS];

  return [Format.DASH_MULTI_PERIOD, Format.DASH_SINGLE_PERIOD];
};

const getSourceType = () => {
  if (browser === 'safari') return SourceType.HLS;
  return SourceType.DASH;
};

const getSupportedLar = () => {
  // Frequency cap ads are only supported on Yospace
  // However, this does fall back to Google DAI on HLS up until Yospace is supported
  if (browser === 'safari') return [AdVendor.YOSPACE_CS];

  return [AdVendor.IGNORE, AdVendor.YOSPACE_CS];
};

const getSupportedDRMLevel = async () => {
  const securityLevel = await getHighestSupportedRobustness();
  return securityLevel;
};

const getMaxBitrate = (castDeviceVersion?: CastDeviceVersion): MaxBitrate[] => {
  // Do not set max BPS on web
  if (!('cast' in window)) return [];

  switch (castDeviceVersion) {
    case CastDeviceVersion.ULTRA_4k:
    case CastDeviceVersion.GOOGLE_TV:
    case CastDeviceVersion.GOOGLE_TV_4k:
      return [];
    default:
      // Cast devices before gen 3 can't handle 1080p
      // Also GEN 3 is getting 4000 BPS to ensure less buffering issues
      return [MaxBitrate.MAX_BPS_4000];
  }
};

type GetMediaCapabilitiesOptions = {
  castDeviceVersion?: CastDeviceVersion;
};

/**
 * Uses the browser capabilities to determine the best DRM and formats to use.
 * @param {GetMediaCapabilitiesOptions} options
 * @param {MediaCapabilityCheckers} checkerOverrides specify some checkers if you want to override their behavior on your platform
 * @returns {Promise<{supported: boolean, formats: Format[], drm: Drm[], codecs: Codec[], dynamicRange: DynamicRange[], resolutions: Resolution[], sourceType: SourceType, lar: AdVendor}>}
 */
export async function getMediaCapabilities(
  options: GetMediaCapabilitiesOptions = {},
  checkerOverrides: Partial<MediaCapabilityCheckers> | undefined = undefined
): Promise<MediaCapabilities> {
  const defaultCheckers: MediaCapabilityCheckers = {
    getSupportedCodecs,
    getSupportedDRM,
    getSupportedDRMLevel,
    getSupportedDynamicRange,
    getSupportedResolutions,
  };
  const checkers = { ...defaultCheckers, ...checkerOverrides };

  const [
    supportedCodecs,
    supportedDRM,
    supportedDRMLevel,
    supportedDynamicRange,
    supportedResolutions,
  ] = await Promise.all([
    checkers.getSupportedCodecs(),
    checkers.getSupportedDRM(),
    checkers.getSupportedDRMLevel(),
    checkers.getSupportedDynamicRange(),
    checkers.getSupportedResolutions(),
  ]);

  return {
    resolution: supportedResolutions,
    codec: supportedCodecs,
    drm: supportedDRM.concat(Drm.NONE),
    drmSecurityLevel: supportedDRMLevel,
    format: getSupportedFormats(),
    sourceType: getSourceType(),
    dynamicRange: supportedDynamicRange,
    maxBPS: getMaxBitrate(options.castDeviceVersion),
    lar: getSupportedLar(),
    hdcpVersion: getHDCPVersion(supportedDRM, supportedDRMLevel),
  };
}

export type MediaCapabilityCheckers = {
  getSupportedCodecs: typeof getSupportedCodecs;
  getSupportedDRM: typeof getSupportedDRM;
  getSupportedDRMLevel: typeof getSupportedDRMLevel;
  getSupportedDynamicRange: typeof getSupportedDynamicRange;
  getSupportedResolutions: typeof getSupportedResolutions;
};
